import { React, useCallback, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageTitle from "../PageTitle";
import useAxios from "../../utilities/useAxios";
import {
	Card,
	Row,
	Col,
	Button,
	Spin,
	message,
	Typography,
	Space,
	Input,
	DatePicker,
	Form,
} from "antd";
import {
	ArrowLeftOutlined,
	UserOutlined,
	CalendarOutlined,
	TeamOutlined,
	SwapOutlined,
	SaveOutlined,
	EditOutlined,
} from "@ant-design/icons";
import SelectCellModal from "./SelectCellModal";
import SelectDepartmentModal from "./SelectDepartmentModal";
import useIsMobile from "../../hooks/useIsMobile";

const { Title, Text } = Typography;
const { TextArea } = Input;

const AddMemberStep2 = () => {
	const isMobile = useIsMobile();
	const { id } = useParams();
	const navigate = useNavigate();
	const [saving, setSaving] = useState(false);
	const [loading, setLoading] = useState(false);
	const [form] = Form.useForm();
	const [memberData, setMemberData] = useState(null);
	const [cellModalVisible, setCellModalVisible] = useState(false);
	const [departmentModalVisible, setDepartmentModalVisible] =
		useState(false);

	const getMemberDetail = useCallback(async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				`/users/user-detail/${id}/`
			);
			if (response.status === 200) {
				setMemberData(response.data);
			}
		} catch (error) {
			message.error("성도 정보를 불러오는데 실패했습니다.");
			navigate("../view-all-members");
		} finally {
			setLoading(false);
		}
	}, [id, navigate]);

	useEffect(() => {
		if (id) {
			getMemberDetail();
		}
	}, [id, getMemberDetail]);

	const handleBack = () => {
		navigate("../view-all-members");
	};

	const handleCellSelect = (cellData) => {
		form.setFieldsValue({
			cell_id: cellData.cellId,
			cell_name: cellData.cellName,
		});
	};

	const handleDepartmentSelect = (departmentData) => {
		form.setFieldsValue({
			affiliated_department_id: departmentData.departmentId,
			affiliated_department_name:
				departmentData.departmentName,
		});
	};

	const handleSave = async () => {
		try {
			setSaving(true);
			const values = await form.validateFields();

			const affiliationsData = {
				user_id: parseInt(id),
				cell_id: values.cell_id || null,
				affiliated_department_id:
					values.affiliated_department_id || null,
				transfer_date: values.transfer_date
					? values.transfer_date.format("YYYY-MM-DD")
					: null,
				corresponding_church: values.corresponding_church || null,
				transfer_reason: values.transfer_reason || null,
			};

			const response = await useAxios.post(
				`/users/update-member-affiliations/`,
				affiliationsData
			);

			if (response.status === 200) {
				message.success(
					"소속 정보가 성공적으로 추가되었습니다."
				);
				navigate("../view-all-members");
			}
		} catch (error) {
			message.error("소속 정보 추가에 실패했습니다.");
			console.error(error);
		} finally {
			setSaving(false);
		}
	};

	const handleSkip = () => {
		message.info("소속 정보 추가를 건너뛰었습니다.");
		navigate("../view-all-members");
	};

	if (loading) {
		return (
			<div className="member-detail__loading">
				<Spin size="large" />
				<Text>데이터를 불러오는 중...</Text>
			</div>
		);
	}

	return (
		<>
			<PageTitle title="성도 소속 정보 추가" />
			<div className="member-detail__container">
				<div className="member-detail__header">
					<Button
						icon={<ArrowLeftOutlined />}
						onClick={handleBack}
						className="member-detail__back-button">
						목록으로 돌아가기
					</Button>
					<Title
						level={2}
						className="member-detail__title">
						성도 소속 정보 추가
					</Title>
					<Space>
						<Button
							onClick={handleSkip}
							className="member-detail__edit-button">
							건너뛰기
						</Button>
						<Button
							icon={<SaveOutlined />}
							onClick={handleSave}
							loading={saving}
							type="primary"
							className="member-detail__edit-button">
							저장
						</Button>
					</Space>
				</div>

				{memberData && (
					<Card
						title={
							<Space>
								<UserOutlined />
								성도 정보
							</Space>
						}
						className="member-detail__card"
						style={{ marginBottom: 16 }}>
						<Row gutter={[24, 16]}>
							<Col xs={24} sm={12} md={8}>
								<Text strong>ID: </Text>
								<Text>{memberData.id}</Text>
							</Col>
							<Col xs={24} sm={12} md={8}>
								<Text strong>성명: </Text>
								<Text>{memberData.name}</Text>
							</Col>
							<Col xs={24} sm={12} md={8}>
								<Text strong>성별: </Text>
								<Text>{memberData.gender || "-"}</Text>
							</Col>
						</Row>
					</Card>
				)}

				<Form form={form} layout="vertical">
					<div className="member-detail__content">
						{/* 소속 정보 섹션 */}
						<Card
							title={
								<Space>
									<TeamOutlined />
									소속
									정보
								</Space>
							}
							className="member-detail__card">
							<Row gutter={[24, 16]}>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<div className="member-detail__info-item">
										<Text className="member-detail__info-label">
											구역:
										</Text>
										<div className="member-detail__info-value">
											<Form.Item
												name="cell_name"
												style={{
													margin: 0,
													flex: 1,
												}}>
												<Input
													placeholder="구역 없음"
													readOnly
												/>
											</Form.Item>
											<Form.Item
												name="cell_id"
												style={{
													display: "none",
												}}>
												<Input type="hidden" />
											</Form.Item>
											<Button
												type="primary"
												size="small"
												icon={
													<EditOutlined />
												}
												onClick={() =>
													setCellModalVisible(
														true
													)
												}
												style={{
													marginLeft: 8,
												}}>
												조회
											</Button>
										</div>
									</div>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<div className="member-detail__info-item">
										<Text className="member-detail__info-label">
											소속기관:
										</Text>
										<div className="member-detail__info-value">
											<Form.Item
												name="affiliated_department_name"
												style={{
													margin: 0,
													flex: 1,
												}}>
												<Input
													placeholder="소속기관 없음"
													readOnly
												/>
											</Form.Item>
											<Form.Item
												name="affiliated_department_id"
												style={{
													display: "none",
												}}>
												<Input type="hidden" />
											</Form.Item>
											<Button
												type="primary"
												size="small"
												icon={
													<EditOutlined />
												}
												onClick={() =>
													setDepartmentModalVisible(
														true
													)
												}
												style={{
													marginLeft: 8,
												}}>
												조회
											</Button>
										</div>
									</div>
								</Col>
							</Row>
						</Card>

						{/* 이명 정보 섹션 */}
						<Card
							title={
								<Space>
									<SwapOutlined />
									이명
									정보
								</Space>
							}
							className="member-detail__card">
							<Row gutter={[24, 16]}>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<div className="member-detail__info-item">
										<Text className="member-detail__info-label">
											이명일:
										</Text>
										<div className="member-detail__info-value">
											<Form.Item
												name="transfer_date"
												style={{
													margin: 0,
													flex: 1,
												}}>
												<DatePicker
													inputReadOnly={isMobile}
													placeholder="이명일 선택"
													style={{
														width: "100%",
													}}
													format="YYYY-MM-DD"
													icon={
														<CalendarOutlined />
													}
												/>
											</Form.Item>
										</div>
									</div>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<div className="member-detail__info-item">
										<Text className="member-detail__info-label">
											이전교회:
										</Text>
										<div className="member-detail__info-value">
											<Form.Item
												name="corresponding_church"
												style={{
													margin: 0,
													flex: 1,
												}}>
												<Input placeholder="이전교회 입력" />
											</Form.Item>
										</div>
									</div>
								</Col>
								<Col
									xs={24}
									sm={12}
									md={8}>
									<div className="member-detail__info-item">
										<Text className="member-detail__info-label">
											이명사유:
										</Text>
										<div className="member-detail__info-value">
											<Form.Item
												name="transfer_reason"
												style={{
													margin: 0,
													flex: 1,
												}}>
												<TextArea
													placeholder="이명사유 입력"
													rows={3}
												/>
											</Form.Item>
										</div>
									</div>
								</Col>
							</Row>
						</Card>
					</div>
				</Form>
			</div>

			{/* Select Cell Modal */}
			<SelectCellModal
				visible={cellModalVisible}
				onCancel={() => setCellModalVisible(false)}
				onSelect={handleCellSelect}
				userGender={memberData?.gender}
			/>

			{/* Select Department Modal */}
			<SelectDepartmentModal
				visible={departmentModalVisible}
				onCancel={() =>
					setDepartmentModalVisible(false)
				}
				onSelect={handleDepartmentSelect}
			/>
		</>
	);
};

export default AddMemberStep2;

